import { TuiDialogService } from '@taiga-ui/core';
import { TUI_CONFIRM } from '@taiga-ui/kit';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { RequestEntitiesService } from './request-entities.service';
import { RequestsApiService } from './requests-api.service';
import { XAlertService } from '../x-alert.service';
import {
  catchError,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import {
  RequestDevice,
  RequestDeviceModelResponse,
  RequestDeviceWithAdditionalSettings,
  RequestDeviceWithoutAdditionalSettings,
} from '@core/types/RequestDevice';
import { differenceInDays, format } from 'date-fns';
import {
  RequestFormDevicesAddDeviceTranslations,
  RequestFormDevicesAddDeviceTranslationsPayload,
  UpdateRequestObdDataPayload,
} from '@core/types';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestDeviceAdditionalSettingsModalComponent } from '@features/requests/request-form/components/request-form-devices/components/request-device-additional-settings-modal/request-device-additional-settings-modal.component';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TuiSheetDialogService } from '@taiga-ui/addon-mobile';
import {
  DeviceInstallQualityCheckStatus,
  DeviceTamperStatus,
} from '@core/types/DeviceInstallQuality';
import { SailsIoService } from '../sails-io.service';
import { DynamicGeolocationService } from '../dynamic-geolocation.service';

@Injectable({
  providedIn: 'root',
})
export class RequestDevicesService extends RequestEntitiesService<
  RequestDevice[]
> {
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  // private readonly warehouseService = inject(WarehouseService);
  private readonly requestsApiService = inject(RequestsApiService);
  private readonly dynamicGeolocationService = inject(
    DynamicGeolocationService,
  );
  private readonly dialogService$ = inject(TuiDialogService);
  private readonly sheetDialogService$ = inject(TuiSheetDialogService);
  private readonly xAlertService = inject(XAlertService);
  private readonly sailsIoService = inject(SailsIoService);
  readonly $request = this.requestService.$request;
  override readonly destroyRef = inject(DestroyRef);
  private translates: Partial<RequestFormDevicesAddDeviceTranslationsPayload> =
    {
      [RequestFormDevicesAddDeviceTranslations.DialogAddDevice]:
        'Вы действительно хотите добавить устройство',
      [RequestFormDevicesAddDeviceTranslations.DialogAddDeviceYes]:
        'Да, добавить',
      [RequestFormDevicesAddDeviceTranslations.DialogAddDeviceNo]: 'Отмена',
      [RequestFormDevicesAddDeviceTranslations.DialogConfirmReleaseDateYes]:
        'Да',
      [RequestFormDevicesAddDeviceTranslations.DialogConfirmReleaseDateNo]:
        'Отмена',
      [RequestFormDevicesAddDeviceTranslations.ErrorGettingDeviceModelId]:
        'Не удалось получить модель устройства. Пожалуйста, попробуйте позже.',
    };

  readonly source$: () => Observable<RequestDevice[]> = () => {
    const { requestNumber } = this.requestService.$request();

    return this.requestsApiService.getDevicesByRequestNumber$(requestNumber);
  };

  constructor() {
    super();
    this.outkeepTranslateService
      .translate([
        RequestFormDevicesAddDeviceTranslations.DialogAddDevice,
        RequestFormDevicesAddDeviceTranslations.DialogAddDeviceYes,
        RequestFormDevicesAddDeviceTranslations.DialogAddDeviceNo,
        RequestFormDevicesAddDeviceTranslations.DialogConfirmReleaseDateYes,
        RequestFormDevicesAddDeviceTranslations.DialogConfirmReleaseDateNo,
        RequestFormDevicesAddDeviceTranslations.ErrorGettingDeviceModelId,
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      // Сливаем с дефолтами: иначе ключи, не попавшие в список, пропадали
      // и алерт об ошибке получения модели устройства был пустым
      .subscribe(
        translates => (this.translates = { ...this.translates, ...translates }),
      );
  }

  // checkWarehouseDeviceSoldAttempt$(
  //   sim1Number: RequestDevice['name'],
  // ): Observable<unknown> {
  //   const installerId = this.requestService.$request().idOfInstaller;
  //
  //   if (!installerId) {
  //     return of(null);
  //   }
  //
  //   return this.warehouseService.checkDeviceSoldAttempt$(
  //     installerId,
  //     this.removePlusAtNumberBegin(sim1Number),
  //   );
  // }

  // removePlusAtNumberBegin<T extends Nullable<string>>(phoneNumber: T): T {
  //   if (!phoneNumber) return phoneNumber;
  //   if (phoneNumber.startsWith('+')) {
  //     return phoneNumber.slice(1) as T;
  //   }
  //
  //   return phoneNumber;
  // }

  getLastLocationDatetimeOf(requestDevice: RequestDevice): Date | null {
    return requestDevice.status?.location_dt
      ? new Date(requestDevice.status.location_dt)
      : null;
  }

  getLastDeviceDatetimeOf(requestDevice: RequestDevice): Date | null {
    return requestDevice.status?.device_dt
      ? new Date(requestDevice.status.device_dt)
      : null;
  }

  getDaysBetweenLocationAndLastDateOf(
    requestDevice: RequestDevice,
  ): number | null {
    const lastDeviceDatetime = this.getLastDeviceDatetimeOf(requestDevice);
    const lastLocationDatetime = this.getLastLocationDatetimeOf(requestDevice);

    if (!lastDeviceDatetime || !lastLocationDatetime) {
      return null;
    }

    return Math.abs(differenceInDays(lastDeviceDatetime, lastLocationDatetime));
  }

  getDeviceInstallQualityStatus$(
    deviceHash: RequestDevice['deviceHash'],
  ): Observable<DeviceInstallQualityCheckStatus> {
    return this.requestsApiService.getDeviceInstallQualityStatus$(deviceHash);
  }

  getDeviceTamperStatus$(
    deviceHash: RequestDevice['deviceHash'],
  ): Observable<DeviceTamperStatus> {
    return this.requestsApiService.getDeviceTamperStatus$(deviceHash);
  }

  scheduleDeviceInstallQualityCheck$(
    requestId: RequestDevice['request'],
    deviceHash: RequestDevice['deviceHash'],
  ) {
    return this.requestsApiService.scheduleDeviceInstallQualityRequest$(
      requestId,
      deviceHash,
    );
  }

  updateRequestObdInfo$(
    requestDeviceId: RequestDevice['id'],
    obdData: UpdateRequestObdDataPayload,
  ): Observable<void> {
    return this.requestsApiService.updateRequestObdInfo$(
      requestDeviceId,
      obdData,
    );
  }

  private checkReleaseDate$(deviceInstallDateValue: Date): Observable<unknown> {
    if (this.$request().releaseDate) {
      const formatVal = 'yyyy-MM-dd';

      const releaseDate = this.$request().releaseDate
        ? format(new Date(this.$request().releaseDate!), formatVal)
        : null;

      const deviceInstallDate = format(
        new Date(deviceInstallDateValue),
        formatVal,
      );

      if (releaseDate !== deviceInstallDate) {
        return this.outkeepTranslateService
          .translate(
            [RequestFormDevicesAddDeviceTranslations.DialogConfirmReleaseDate],
            { deviceInstallDate, releaseDate },
          )
          .pipe(
            switchMap(translates => {
              return this.dialogService$
                .open<boolean>(TUI_CONFIRM, {
                  label:
                    translates[
                      RequestFormDevicesAddDeviceTranslations
                        .DialogConfirmReleaseDate
                    ],
                  size: 's',
                  closable: false,
                  data: {
                    yes: this.translates[
                      RequestFormDevicesAddDeviceTranslations
                        .DialogConfirmReleaseDateYes
                    ],
                    no: this.translates[
                      RequestFormDevicesAddDeviceTranslations
                        .DialogConfirmReleaseDateNo
                    ],
                  },
                })
                .pipe(filter(Boolean));
            }),
          );
      }
    }

    return of(undefined);
  }

  private submitRequestBrowserCoords$() {
    return this.dynamicGeolocationService
      .getAccuratePositionWithFallback$({
        accurateTimeoutSeconds: 25,
        fallbackTimeoutSeconds: 5,
        maximumAgeMinutes: 5,
      })
      .pipe(
        take(1),
        switchMap(position =>
          position.coords.longitude && position.coords.latitude
            ? this.requestsApiService.submitInstallerBrowserCoords$(
                this.$request().id,
                position,
              )
            : of(null),
        ),
        catchError((err: HttpErrorResponse) => {
          if (err.status === HttpStatusCode.BadRequest) {
            this.xAlertService.showMessage(err.error, {
              appearance: 'negative',
              autoClose: 10000,
            });

            return of(null);
          }

          return throwError(() => err);
        }),
      );
  }

  private openModalWIthAdditionalDeviceSettings(
    deviceData:
      | RequestDeviceWithoutAdditionalSettings
      | Omit<RequestDeviceWithoutAdditionalSettings, 'installDate'>
      | RequestDeviceWithAdditionalSettings,
    isMobile: boolean,
  ): Observable<RequestDeviceWithAdditionalSettings> {
    if (isMobile) {
      return this.sheetDialogService$.open<RequestDeviceWithAdditionalSettings>(
        new PolymorpheusComponent(
          RequestDeviceAdditionalSettingsModalComponent,
        ),
        {
          closable: true,
          data: {
            deviceData,
          },
        },
      );
    }

    return this.dialogService$.open<RequestDeviceWithAdditionalSettings>(
      new PolymorpheusComponent(RequestDeviceAdditionalSettingsModalComponent),
      {
        size: null,
        closable: true,
        dismissible: true,
        data: {
          deviceData,
        },
      },
    );
  }

  public addNewDeviceToRequest$(
    deviceData: RequestDeviceWithoutAdditionalSettings,
    isMobile: boolean,
  ) {
    return this.checkReleaseDate$(deviceData.installDate).pipe(
      switchMap(() =>
        this.openModalWIthAdditionalDeviceSettings(deviceData, isMobile),
      ),
      switchMap(deviceDataWithAdditionalSettings =>
        this.dialogService$
          .open<boolean>(TUI_CONFIRM, {
            label:
              this.translates[
                RequestFormDevicesAddDeviceTranslations.DialogAddDevice
              ],
            size: 's',
            closable: false,
            data: {
              yes: this.translates[
                RequestFormDevicesAddDeviceTranslations.DialogAddDeviceYes
              ],
              no: this.translates[
                RequestFormDevicesAddDeviceTranslations.DialogAddDeviceNo
              ],
            },
          })
          .pipe(
            map(confirmed => ({ confirmed, deviceDataWithAdditionalSettings })),
          ),
      ),
      filter(({ confirmed }) => confirmed),
      switchMap(payload =>
        this.submitRequestBrowserCoords$().pipe(
          map(() => payload),
          catchError(() => of(payload)),
        ),
      ),
      switchMap(({ deviceDataWithAdditionalSettings }) =>
        this.requestsApiService.addDeviceToRequest$(this.$request().id, {
          name: deviceDataWithAdditionalSettings.name!,
          secondSim: deviceDataWithAdditionalSettings.secondSim,
          hasProtectCase: deviceDataWithAdditionalSettings.hasProtectCase,
          protectCaseNumber: deviceDataWithAdditionalSettings.protectCaseNumber,
          installDate: deviceDataWithAdditionalSettings.installDate,
          onboardVoltage: deviceDataWithAdditionalSettings.onboardVoltage,
          mountType: deviceDataWithAdditionalSettings.mountType,
          cutoffSwitch: deviceDataWithAdditionalSettings.cutoffSwitch,
        }),
      ),
      catchError((err: HttpErrorResponse) => {
        if (err.status === HttpStatusCode.BadRequest) {
          this.xAlertService.showMessage(err.error, {
            appearance: 'negative',
            autoClose: 10000,
          });

          return EMPTY;
        }

        return throwError(() => err);
      }),
    );
  }

  public updateRequestDevice$(
    requestDeviceId: RequestDevice['id'],
    deviceData: Omit<RequestDeviceWithoutAdditionalSettings, 'installDate'>,
    isMobile: boolean,
  ): Observable<RequestDevice> {
    return this.openModalWIthAdditionalDeviceSettings(
      deviceData,
      isMobile,
    ).pipe(
      switchMap(deviceDataWithAdditionalSettings =>
        this.requestsApiService
          .updateRequestDevice$(requestDeviceId, {
            name: deviceDataWithAdditionalSettings.name!,
            secondSim: deviceDataWithAdditionalSettings.secondSim,
            hasProtectCase: deviceDataWithAdditionalSettings.hasProtectCase,
            protectCaseNumber:
              deviceDataWithAdditionalSettings.protectCaseNumber,
            onboardVoltage: deviceDataWithAdditionalSettings.onboardVoltage,
            mountType: deviceDataWithAdditionalSettings.mountType,
            cutoffSwitch: deviceDataWithAdditionalSettings.cutoffSwitch,
          })
          .pipe(
            catchError((err: HttpErrorResponse) => {
              if (err.status === HttpStatusCode.BadRequest) {
                this.xAlertService.showMessage(err.error, {
                  appearance: 'negative',
                  autoClose: 10000,
                });

                return EMPTY;
              }

              return throwError(() => err);
            }),
          ),
      ),
    );
  }

  public getDeviceModelId$(
    sim1: RequestDevice['name'],
  ): Observable<RequestDeviceModelResponse | undefined> {
    return this.requestsApiService.getDeviceModelId$(sim1).pipe(
      catchError(() => {
        this.xAlertService.showMessage(
          this.translates[
            RequestFormDevicesAddDeviceTranslations.ErrorGettingDeviceModelId
          ],
          {
            appearance: 'negative',
            autoClose: 5000,
          },
        );

        return of(undefined);
      }),
    );
  }

  public listenToInstallQualityUpdates$(
    deviceHash: RequestDevice['deviceHash'],
  ): Observable<DeviceInstallQualityCheckStatus> {
    return this.sailsIoService.listen$<DeviceInstallQualityCheckStatus>(
      `installQualityCheck/${deviceHash}`,
      () =>
        this.sailsIoService.post$(
          `/logistic/request/devices/${deviceHash}/installQuality/subscription`,
        ),
      () =>
        this.sailsIoService.delete$(
          `/logistic/request/devices/${deviceHash}/installQuality/subscription`,
        ),
    );
  }
}
